import React, { memo, useCallback, useEffect, useState } from 'react';
import GridConfig from './grid-config';
import DataGrid from 'components/data-grid';
import { useFormGrid } from 'AurionCR/components/formV2';
import clsx from 'clsx';
import style from './index.module.scss';
import { SectionPaper } from '../../../section-papper';
import { useTranslate } from 'hooks/use-translate';
import { signedDocumentEmitter } from '../../emitter';
import { AddNewDocument } from 'components/add-new-document';
import { Box } from '@mui/material';

interface Props {
  userPatientProfileID: string | undefined | null;
  prescriptionID: string;
  onRefresh: () => void;
  className: string;
}
export const GridSignedDocuments = memo<Props>(
  ({ prescriptionID, userPatientProfileID, onRefresh, className }) => {
    const { t } = useTranslate();
    const [gridConfig] = useState<any>(GridConfig(prescriptionID));
    const { grid, onGridHandle } = useFormGrid('document');

    const onRefreshGrid = useCallback(() => {
      grid.current?.onTasks([['getData']]);
    }, [grid]);

    const onGridHandleWrapper = useCallback(
      (data: any) => {
        onGridHandle(data);
        if (data.type === 'deleteDone') {
          onRefresh();
        }
      },
      [onGridHandle, onRefresh],
    );

    useEffect(() => {
      const refresh = (options: { prescriptionID: string }) => {
        if (options.prescriptionID === prescriptionID) {
          onRefreshGrid();
        }
      };

      signedDocumentEmitter.on('refresh-grid', refresh);
      return () => {
        signedDocumentEmitter.off('refresh-grid', refresh);
      };
    }, [onRefreshGrid, prescriptionID]);

    const renderControlsSlot = useCallback(() => {
      if (!userPatientProfileID) {
        return null;
      }
      return (
        <Box
          sx={{
            mx: 1,
          }}
        >
          <AddNewDocument
            userPatientProfileID={userPatientProfileID}
            postData={{
              userPatientProfilePrescriptionID: prescriptionID,
            }}
            onDone={onRefreshGrid}
          />
        </Box>
      );
    }, [userPatientProfileID, prescriptionID, onRefreshGrid]);

    return (
      <SectionPaper className={clsx(style.root, className)} title={t('document-to-sign')}>
        <DataGrid
          ref={grid}
          onHandle={onGridHandleWrapper}
          config={gridConfig}
          renderControlsSlot={renderControlsSlot}
        />
      </SectionPaper>
    );
  },
);
